<template>
  <c-box mt="4xl">
    <main
      id="content"
      v-chakra
      :mt="{ base: 'xl', sm: '4xl', md: 0 }"
      :mx="{ base: '-16px', sm: 0 }"
    >
      <submitter-details-mobile
        v-if="returnData && returnData.productReturnData"
        :contact-details="returnData.productReturnData.contactDetails"
      />

      <c-box :my="{ base: 'md', md: '3.5rem' }">
        <c-heading
          as="h1"
          :font-size="{ base: '2xl', md: '4xl' }"
          mt="md"
          mb="xs"
          color="brand.purpleDark"
          font-weight="normal"
        >
          {{ $t("productReturn.displaySingle.salesReturn") }}
        </c-heading>
        <c-heading as="h2" font-size="md" color="deepBlack" font-weight="normal">
          {{ displayId }}
        </c-heading>
      </c-box>

      <c-divider width="100%" border-color="gray.300" :mb="{ base: 'md', md: '3xl', lg: '4xl' }" />

      <c-flex v-if="isLoading" width="100%" justify-content="center">
        <Spinner />
      </c-flex>
      <c-box v-else-if="returnData.productReturnData">
        <c-flex
          :gap="returnData.shipperType !== 'DHL' && { base: '2', md: '6', lg: '12', xl: '16' }"
          :flex-direction="{
            base: 'column',
            md: returnData.shipperType === 'DHL' ? 'column' : 'row',
          }"
        >
          <c-grid
            :template-columns="{ md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }"
            :columnGap="{ base: '6', lg: '12', xl: '16' }"
            :rowGap="{ base: '6', sm: '10' }"
            :mb="{ base: 'lg', md: '3xl', lg: returnData.shipperType === 'DHL' ? '3xl' : '4xl' }"
          >
            <!-- submitted by -->
            <c-grid-item>
              <c-heading as="h3" size="sm" :mb="{ base: 'xs', sm: 'sm' }">
                {{ $t("productReturn.displaySingle.submitted") }}
              </c-heading>
              <c-text font-weight="350">
                {{
                  $t("productReturn.displaySingle.submittedBy", {
                    date: moment(returnData.created).format("D.M.YYYY"),
                    creator: returnData.productReturnData.contactDetails.fullName,
                  })
                }}
              </c-text>
            </c-grid-item>

            <!-- order number -->
            <c-grid-item>
              <c-heading as="h3" size="sm" :mb="{ base: 'xs', sm: 'sm' }">
                {{ $t("productReturn.salesReturn.orderNumber") }}
              </c-heading>
              <c-text font-weight="350">
                {{
                  (returnData.productReturnData.productDetails &&
                    returnData.productReturnData.productDetails.orderNumber) ||
                  "-"
                }}
              </c-text>
            </c-grid-item>

            <!-- status -->
            <c-grid-item :col-span="[1, 2, 2, 1]">
              <c-heading as="h3" size="sm" :mb="{ base: 'xs', sm: 'sm' }">
                {{ $t("productReturn.displaySingle.overallStatus") }}
              </c-heading>
              <c-box>
                <c-box
                  mb="sm"
                  v-chakra="{
                    '.return-progress > div': {
                      backgroundColor: 'brand.purpleLight',
                    },
                  }"
                >
                  <c-progress
                    class="return-progress"
                    has-stripe
                    :value="100"
                    border-radius="full"
                    size="lg"
                    background="gray.200"
                    width="100%"
                    min-width="5rem"
                    max-width="12rem"
                  />
                </c-box>
                <c-text font-weight="350">
                  {{ $t("productReturn.note.salesReturnStatusesMissing") }}
                </c-text>
              </c-box>
            </c-grid-item>
          </c-grid>
          <c-box v-if="returnData.shipperType === 'DHL'" :mb="{ base: 'sm', md: '2xl', lg: '3xl' }">
            <RMADHLShippingInformation
              :tracking-codes="returnData.parcels"
              :planned-shipping-date="
                returnData.productReturnData.returnDetails.plannedShippingDate
              "
              :country-code="returnData.productReturnData.contactDetails.partnerCountry"
              :zip-code="returnData.productReturnData.contactDetails.partnerZipCode"
              :return-type="returnData.productReturnData.returnType"
              :rma-id="returnData.rmaId"
              :parcel-label-and-pro-forma-id="returnData.rmaId"
              :pro-forma-available="returnData.proForma"
            />
          </c-box>
          <c-box
            v-else-if="returnData.shipperType === 'CONTACT_CS'"
            :mb="{ base: 'sm', md: '2xl', lg: '3xl' }"
          >
            <c-icon name="info" size="1.8rem" color="brand.black" m="-6px 0 0 -4px" />{{
              $t("productReturnV2.details.contactCsInfo")
            }}
          </c-box>
          <c-box
            v-else-if="returnData.shipperType !== 'MANUAL'"
            :width="{ md: '33.333%', xl: '25%' }"
          >
            <c-heading as="h3" size="sm" :mb="{ base: 'xs', sm: 'sm' }">
              {{ $t("productReturn.displaySingle.printParcelLabel") }}
            </c-heading>
            <c-box>
              <RMADownloadParcel
                :return-id="returnData.rmaId"
                :return-type="returnData.productReturnData.returnType"
                v-chakra
                :width="{ md: '100%' }"
                max-width="20rem"
                mb="xs"
                display="block"
              />
              <RMADownloadInternationalForm
                v-if="returnData.proForma"
                :return-id="returnData.rmaId"
                :return-type="returnData.productReturnData.returnType"
                v-chakra
                :width="{ md: '100%' }"
                max-width="20rem"
                mb="xs"
                display="block"
              />
              <template v-if="returnData.parcels && returnData.parcels.length > 0">
                <RMAParcelTrackingLink
                  :rma-id="returnData.rmaId"
                  link-type="link"
                  v-chakra
                  :width="{ md: '100%' }"
                  max-width="20rem"
                  mb="xs"
                  display="block"
                />
              </template>
            </c-box>
          </c-box>
        </c-flex>

        <c-divider
          width="100%"
          border-color="gray.300"
          :mb="{ base: 'md', md: '3xl', lg: '3rem' }"
        />

        <c-box :mb="{ base: 'md', md: '3xl', lg: '4xl' }">
          <c-heading as="h3" font-size="2xl" :mb="{ md: 'xl' }">
            {{ $t("productReturnV2.returnedProducts") }}
          </c-heading>

          <c-box
            :d="{ base: 'none', md: 'grid' }"
            :grid-template-columns="{ base: '3fr 5fr 2fr 2fr', lg: '2fr 6fr 2fr 2fr' }"
            border-bottom="1px solid"
            border-bottom-color="gray.300"
            py="xl"
          >
            <c-text>
              {{ $t("productReturn.salesReturn.productSKUs") }}
            </c-text>
            <c-text>
              {{ $t("productReturn.salesReturn.productName") }}
            </c-text>
            <c-text>
              {{ $t("productReturn.salesReturn.orderQuantity") }}
            </c-text>
            <c-text>
              {{ $t("productReturn.salesReturn.returnQuantity") }}
            </c-text>
          </c-box>

          <c-grid
            v-for="product in (
              (returnData.productReturnData.productDetails &&
                returnData.productReturnData.productDetails.products) ||
              []
            ).filter((p) => p.returnCount > 0)"
            :key="product.sku"
            :d="{ md: 'grid' }"
            :grid-template-columns="{ base: '3fr 5fr 2fr 2fr', lg: '2fr 6fr 2fr 2fr' }"
            border-bottom="1px solid"
            border-bottom-color="gray.300"
            py="xl"
          >
            <c-box v-if="product.id">
              <c-text as="span" :d="{ md: 'none' }">
                {{ $t("productReturn.displaySingle.productSKUs") }}:
              </c-text>
              <!-- Old sales returns in previous versions before January 2021 do not contain sku -->
              {{ product.id || "-" }}
            </c-box>
            <c-box>
              <c-text as="span" :d="{ md: 'none' }">
                {{ $t("productReturn.salesReturn.productName") }}:
              </c-text>
              {{ product.name }}
            </c-box>
            <c-box>
              <c-text as="span" :d="{ md: 'none' }">
                {{ $t("productReturn.salesReturn.orderQuantity") }}:
              </c-text>
              {{ product.orderCount }}
            </c-box>
            <c-box>
              <c-text as="span" :d="{ md: 'none' }">
                {{ $t("productReturn.salesReturn.returnQuantity") }}:
              </c-text>
              {{ product.returnCount }}
            </c-box>
          </c-grid>
          <submitter-details :contact-details="returnData.productReturnData.contactDetails" />
        </c-box>
      </c-box>
    </main>
  </c-box>
</template>

<script lang="ts">
import SubmitterDetails from "@/components/returns/SubmitterDetails.vue";
import SubmitterDetailsMobile from "@/components/returns/SubmitterDetailsMobile.vue";
import Spinner from "@/components/Spinner.vue";
import RMADHLShippingInformation from "@/views/rma/RMADHLShippingInformation.vue";
import RMADownloadInternationalForm from "@/views/rma/RMADownloadInternationalForm.vue";
import RMADownloadParcel from "@/views/rma/RMADownloadParcel.vue";
import RMAParcelTrackingLink from "@/views/rma/RMAParcelTrackingLink.vue";
import RMAProcessSteps from "@/views/rma/RMAProcessSteps.vue";
import {
  CBox,
  CDivider,
  CFlex,
  CGrid,
  CGridItem,
  CHeading,
  CIcon,
  CProgress,
  CText,
} from "@chakra-ui/vue";
import { IResultEntity } from "@iloq/shared";
import { defineComponent } from "vue";
import { mapState } from "vuex";

interface IData {
  isLoading: boolean;
  returnData: IResultEntity | Record<string, never>;
  displayId?: string;
  moment: any;
}

export default defineComponent({
  name: "SalesReturnDetails",
  components: {
    CIcon,
    RMADownloadParcel,
    RMAParcelTrackingLink,
    RMADownloadInternationalForm,
    RMAProcessSteps,
    CBox,
    CDivider,
    CHeading,
    SubmitterDetails,
    SubmitterDetailsMobile,
    Spinner,
    CFlex,
    CText,
    CGrid,
    CGridItem,
    CProgress,
    RMADHLShippingInformation,
  },
  props: {
    rmaId: {
      type: String,
      required: true,
    },
  },
  data(): IData {
    return {
      isLoading: false,
      returnData: {},
      displayId: "",
      moment: (this as any).$moment,
    };
  },
  computed: mapState({
    returnListing: (state) => (state as any).returns.list,
  }),
  mounted(): void {
    this.syncReturnData(this.rmaId);
  },
  methods: {
    syncReturnData(rmaId: string): void {
      this.isLoading = true;
      this.$store.watch(
        (state: any) => state.returns.list.find((item: IResultEntity) => item.rmaId === rmaId),
        (newValue: any) => {
          this.updateReturnData(newValue);
        }
      );

      const returnData = this.returnListing.find((item: IResultEntity) => item.rmaId === rmaId);
      if (returnData) {
        this.updateReturnData(returnData);
      }
    },
    updateReturnData(returnData: IResultEntity): void {
      this.returnData = returnData;
      this.displayId = returnData.rmaId?.substring(0, 7);
      this.isLoading = false;
    },
  },
});
</script>
